/* eslint-disable max-len */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";
import AutoSizer from "react-virtualized/dist/commonjs/AutoSizer";
import KeplerGl from "kepler.gl";
// import KeplerGlSchema from "kepler.gl/schemas";
import * as kglActions from "kepler.gl/actions";
import { processGeojson, processRowObject } from "kepler.gl/processors";
// import { injectComponents, PanelHeaderFactory } from "kepler.gl/components";
// import useSwr from "swr";

import { apiMapbox } from "./../shared/config/config";
import SidePanel from "./KeplerGLMap/SidePanel";
import DetailsPanel from "./DetailsPanel/DetailsPanel";
import TooltipPanel from "./TooltipPanel/TooltipPanel";

import "./KeplerGLMap.scss";
import Chart from "./Chart/Chart";
import {
  statsBooleanColorPalette,
  statsColorPalette,
} from "../store/reducers/dataReducer";

const analyzeTypes = {
  analyzeStores: {
    value: "analyzeStores",
    text: "Analyze Stores",
  },
  analyzeDistricts: {
    value: "analyzeDistricts",
    text: "Analyze Districts",
  },
};

const datasetsInfo = {
  districtsLayer: {
    label: "Hongkong-Districts",
    id: "hongkongDistricts",
  },
  storesLayer: {
    label: "Hongkong-Stores",
    id: "hongkongStores",
  },
  analyzeDistrictsLayer: {
    label: "Consumer-Viewer",
    id: "consumerViewer",
  },
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const KeplerGLMap = (props) => {
  const {
    keplerGl,
    geoDistricts,
    stores,
    storeStats,
    districts,
    districtStats,
    selectedAnalyzeType,
    selectedAttribute,
  } = props;

  const { kglMap } = keplerGl;
  const { datasets, clicked, hoverInfo, layers } = kglMap
    ? kglMap.visState
    : {};
  const mapZoomLevel = kglMap
    ? +Number.parseFloat(kglMap.mapState.zoom).toPrecision(3)
    : 10;

  // const mapWrapperRef = useRef();

  const dispatch = useDispatch();

  /** Custom map style * */
  const {
    mapApiToken,
    mapHost,
    mapAccount,
    mapStyleID_Light,
    mapStyleLabel_Light,
  } = apiMapbox;
  const apiToken = mapApiToken;
  const apiHost = mapHost;
  const mapAcct = mapAccount;
  const mapID = mapStyleID_Light;
  const mapLabel = mapStyleLabel_Light;
  const mapUrl = `mapbox://styles/${mapAcct}/${mapID}`;
  const mapIcon = `${apiHost}/styles/v1/${mapAcct}/${mapID}/static/114.07,22.37,9.19,0,0/400x300?access_token=${apiToken}&logo=false&attribution=false`;
  const mapStyles = [
    {
      id: `${mapID}`,
      label: `${mapLabel}`,
      url: `${mapUrl}`,
      icon: `${mapIcon}`,
      layerGroups: [
        {
          slug: "border",
          filter: ({ id }) => id.match(/(?=(border))/),
          defaultVisibility: true,
        },
      ],
    },
  ];

  const colorizeStore = (store) => {
    const value = store.data[5][selectedAttribute];
    let { min, max, type } = storeStats[selectedAttribute];
    if (type === "boolean") {
      return statsBooleanColorPalette[value];
    } else if (type === "percentage") {
      const step = (max - min) / 5;
      const index = Math.floor((value * 100 - min) / step);
      return statsColorPalette[index];
    } else {
      let index = 0;
      if (selectedAttribute === "Monthly Visitor") {
        index = Math.floor(value / 1500);
      } else {
        index = Math.floor(value / 150);
      }
      return statsColorPalette[index];
    }
  };

  const colorizeDistrict = (district) => {
    const value = district.data[4][selectedAttribute];
    let { min, max, type } = districtStats[selectedAttribute];
    if (type === "boolean") {
      return statsBooleanColorPalette[value];
    } else if (type === "percentage") {
      const step = (max - min) / 5;
      const index = Math.floor((value * 100 - min) / step);
      return statsColorPalette[index];
    } else {
      let index = 0;
      if (selectedAttribute === "Monthly Visitor") {
        index = Math.floor(value / 3400);
      } else {
        index = Math.floor(value / 350);
      }
      return statsColorPalette[index];
    }
  };

  const onMapViewStateChange = (prop) => {
    // console.log("|||||||||| Map view state |||||||||", prop);
  };

  useEffect(() => {
    /* * Apply custom map style * */
    dispatch(kglActions.inputMapStyle(mapStyles[0]));
    dispatch(kglActions.addCustomMapStyle());
    return () => {};
  }, []);

  /*if (kglMap && kglMap.visState.layers.length > 0) {
    console.log("++++++ layers:", layers);
  }*/

  const updateLayerSetting = (
    oldLayer = {},
    newLayerConfig = {},
    newLayerVisConfig = {}
  ) => {
    if (!isEmpty(oldLayer)) {
      if (!isEmpty(newLayerConfig)) {
        dispatch(kglActions.layerConfigChange(oldLayer, newLayerConfig));
      }
      if (!isEmpty(newLayerVisConfig)) {
        dispatch(kglActions.layerVisConfigChange(oldLayer, newLayerVisConfig));
      }
    }
  };

  const globalLayerConfig = {
    visState: {
      interactionConfig: {
        tooltip: { enabled: false },
      },
    },
  };

  /* ******** Add district data to map ******** */
  useEffect(() => {
    if (!geoDistricts) return;
    dispatch(
      kglActions.addDataToMap({
        datasets: {
          info: {
            label: datasetsInfo.districtsLayer.label,
            id: datasetsInfo.districtsLayer.id,
          },
          data: processGeojson(geoDistricts),
        },
        option: {
          centerMap: false,
          readOnly: true,
        },
        config: globalLayerConfig,
      })
    );
  }, [dispatch, geoDistricts]);

  /* * Change settings after district data added * */
  let districtsVisConfigSettings = {};
  let _districtsNewConfig = {};
  let _districtsNewVisConfig = {};
  if (kglMap && kglMap.visState.layers.length > 0) {
    const _layer = layers.find(
      (layer) => layer.config.dataId === datasetsInfo.districtsLayer.id
    );
    if (_layer) {
      districtsVisConfigSettings = _layer.visConfigSettings;
      _districtsNewConfig = {
        animation: { enabled: true },
        color: [255, 203, 153],
        highlightColor: [38, 41, 43, 255],
      };
      _districtsNewVisConfig = {
        filled: true,
        opacity: 0.01,
        strokeColor: [221, 178, 124],
      };
    }
  }

  useEffect(() => {
    if (kglMap && kglMap.visState.layers.length > 0) {
      const _theLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.districtsLayer.id
      );
      updateLayerSetting(
        _theLayer,
        _districtsNewConfig,
        _districtsNewVisConfig
      );
    }
  }, [districtsVisConfigSettings]);
  /* ***************************************** */

  /* ******** Add store data to map ******** */
  useEffect(() => {
    if (!stores) return;
    dispatch(
      kglActions.addDataToMap({
        datasets: {
          info: {
            label: datasetsInfo.storesLayer.label,
            id: datasetsInfo.storesLayer.id,
          },
          data: processRowObject(stores),
        },
        option: {
          centerMap: false,
          readOnly: true,
        },
        config: {},
      })
    );
  }, [dispatch, stores]);

  /* * Change settings after HK stores data added * */
  const setRadius = () => {
    if (mapZoomLevel >= 11) {
      return 35;
    }
    if (mapZoomLevel > 10) {
      return 25;
    }
    if (mapZoomLevel > 9) {
      return 15;
    }
    return 4;
  };

  let storesVisConfigSettings = {};
  let _storesNewConfig = {};
  let _storesNewVisConfig = {};
  if (kglMap && kglMap.visState.layers.length > 0) {
    const _layer = layers.find(
      (layer) => layer.config.dataId === datasetsInfo.storesLayer.id
    );
    if (_layer) {
      storesVisConfigSettings = _layer.visConfigSettings;
      _storesNewConfig = {
        animation: { enabled: true },
        color: [130, 154, 227],
        highlightColor: [40, 40, 40, 255],
        isVisible: mapZoomLevel > 9.6,
      };
      _storesNewVisConfig = {
        radius: setRadius(),
        opacity: 0.5,
        outline: true,
        strokeColor: [106, 116, 133],
      };
    }
  }

  useEffect(() => {
    if (kglMap && kglMap.visState.layers.length > 0) {
      const _theLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.storesLayer.id
      );
      updateLayerSetting(_theLayer, _storesNewConfig, _storesNewVisConfig);
    }
  }, [storesVisConfigSettings]);
  /* *************************************** */

  useEffect(() => {
    if (!districts) return;
    dispatch(
      kglActions.addDataToMap({
        datasets: {
          info: {
            label: datasetsInfo.analyzeDistrictsLayer.label,
            id: datasetsInfo.analyzeDistrictsLayer.id,
          },
          data: processRowObject(districts),
        },
        option: {
          centerMap: false,
          readOnly: true,
        },
        config: {},
      })
    );
  }, [dispatch, districts]);

  let analyzeDistrictsVisConfigSettings = {};
  let _analyzeDistrictsNewConfig = {};
  let _analyzeDistrictsNewVisConfig = {};
  if (kglMap && kglMap.visState.layers.length > 0) {
    const _layer = layers.find(
      (layer) => layer.config.dataId === datasetsInfo.analyzeDistrictsLayer.id
    );
    if (_layer) {
      analyzeDistrictsVisConfigSettings = _layer.visConfigSettings;
      _analyzeDistrictsNewConfig = {
        animation: { enabled: true },
        color: [130, 154, 227],
        highlightColor: [40, 40, 40, 255],
        isVisible: false,
      };
      _analyzeDistrictsNewVisConfig = {
        radius: setRadius(),
        opacity: 0.5,
        outline: true,
        strokeColor: [106, 116, 133],
      };
    }
  }

  useEffect(() => {
    if (kglMap && kglMap.visState.layers.length > 0) {
      const _theLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.analyzeDistrictsLayer.id
      );
      if (_theLayer) {
        updateLayerSetting(
          _theLayer,
          _analyzeDistrictsNewConfig,
          _analyzeDistrictsNewVisConfig
        );
      }
    }
  }, [analyzeDistrictsVisConfigSettings]);

  /* ******** Update config when zoom level changes ******** */
  useEffect(() => {
    if (kglMap && kglMap.visState.layers.length > 0) {
      /** Districts layer * */
      /*const _theDistrictsLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.districtsLayer.id
      );
      const _theDistrictsNewVisConfig = {
        filled: !(mapZoomLevel > 10),
        opacity: mapZoomLevel > 10 ? 0 : 0.01,
      };
      updateLayerSetting(_theDistrictsLayer, {}, _theDistrictsNewVisConfig);
      */
      /** Stores layer * */
      if (selectedAnalyzeType === analyzeTypes.analyzeStores.value) {
        const storesLayer = layers.find(
          (layer) => layer.config.dataId === datasetsInfo.storesLayer.id
        );
        updateLayerSetting(
          storesLayer,
          {
            isVisible: mapZoomLevel > 9.6,
          },
          {
            radius: setRadius(),
          }
        );
      } else if (selectedAnalyzeType === analyzeTypes.analyzeDistricts.value) {
        const districtsLayer = layers.find(
          (layer) =>
            layer.config.dataId === datasetsInfo.analyzeDistrictsLayer.id
        );
        updateLayerSetting(
          districtsLayer,
          {
            isVisible: mapZoomLevel > 9.6,
          },
          {
            radius: setRadius(),
          }
        );
      } else {
        const storesLayer = layers.find(
          (layer) => layer.config.dataId === datasetsInfo.storesLayer.id
        );
        updateLayerSetting(
          storesLayer,
          {
            isVisible: mapZoomLevel > 9.6,
          },
          {
            radius: setRadius(),
          }
        );
      }
    }
  }, [mapZoomLevel]);
  /* ********************************************************* */

  /*if (kglMap) {
    const configToSave = KeplerGlSchema.getConfigToSave(kglMap);
    console.log("====== configToSave", configToSave);
  }*/

  /* * Set initial map zoom level after datasets loaded * */
  useEffect(() => {
    if (kglMap && !isEmpty(kglMap.visState.datasets)) {
      dispatch(
        kglActions.updateMap({
          zoom: 10,
          latitude: 22.3574957,
          longitude: 114.035434,
        })
      );
    }
  }, [datasets]);

  const prevSelectedAnalyzeType = usePrevious(selectedAnalyzeType);

  useEffect(() => {
    if (prevSelectedAnalyzeType && selectedAnalyzeType === "") {
      // back to initial state
      const storesLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.storesLayer.id
      );
      updateLayerSetting(
        storesLayer,
        {
          isVisible: true,
          color: [130, 154, 227],
        },
        {
          outline: true,
          radius: setRadius(),
        }
      );
      const districtsLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.analyzeDistrictsLayer.id
      );
      updateLayerSetting(districtsLayer, {
        isVisible: false,
      });
    } else if (selectedAnalyzeType) {
      const storesLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.storesLayer.id
      );
      const districtsLayer = layers.find(
        (layer) => layer.config.dataId === datasetsInfo.analyzeDistrictsLayer.id
      );
      if (selectedAnalyzeType === analyzeTypes.analyzeStores.value) {
        // switch to stores
        updateLayerSetting(
          storesLayer,
          {
            isVisible: true,
            color: [130, 154, 227],
          },
          { outline: true, radius: setRadius() }
        );
        updateLayerSetting(districtsLayer, {
          isVisible: false,
        });
      } else if (selectedAnalyzeType === analyzeTypes.analyzeDistricts.value) {
        // switch to districts
        updateLayerSetting(storesLayer, {
          isVisible: false,
        });
        updateLayerSetting(
          districtsLayer,
          {
            isVisible: true,
            color: [130, 154, 227],
          },
          {
            outline: true,
            radius: setRadius(),
          }
        );
      }
    }
  }, [selectedAnalyzeType]);

  useEffect(() => {
    if (selectedAttribute) {
      if (selectedAnalyzeType === analyzeTypes.analyzeStores.value) {
        const storesLayer = layers.find(
          (layer) => layer.config.dataId === datasetsInfo.storesLayer.id
        );
        updateLayerSetting(
          storesLayer,
          {
            color: (store) => colorizeStore(store),
          },
          {
            outline: false,
            radius: setRadius() + 5,
          }
        );
      } else if (selectedAnalyzeType === analyzeTypes.analyzeDistricts.value) {
        const districtsLayer = layers.find(
          (layer) =>
            layer.config.dataId === datasetsInfo.analyzeDistrictsLayer.id
        );
        updateLayerSetting(
          districtsLayer,
          {
            color: (district) => colorizeDistrict(district),
          },
          {
            outline: false,
            radius: setRadius() + 5,
          }
        );
      }
    }
  }, [selectedAttribute]);

  const sidePanelProps = {
    dispatch,
    keplerGl,
    datasetsInfo,
    updateLayerSetting,
    storeStats,
    districtStats,
    analyzeTypes,
    selectedAnalyzeType,
    selectedAttribute,
  };

  const tooltipPanelProps = {
    hoverInfo,
  };

  const detailsPanelProps = {
    clicked,
    districts,
  };

  return (
    <div className="keplerGLMap">
      <AutoSizer>
        {({ width, height }) => (
          <KeplerGl
            id="kglMap"
            mapboxApiAccessToken={apiToken}
            mapStylesReplaceDefault
            mapStyles={mapStyles}
            width={width}
            height={height}
            theme="light"
            onViewStateChange={onMapViewStateChange}
          />
        )}
      </AutoSizer>
      {hoverInfo &&
        hoverInfo.picked &&
        (!hoverInfo.object.data || hoverInfo.object.data.length !== 5) && (
          <TooltipPanel {...tooltipPanelProps} />
        )}
      {clicked && <DetailsPanel {...detailsPanelProps} />}
      <SidePanel {...sidePanelProps} />
      {props.selectedAttribute && (
        <Chart
          stats={
            selectedAnalyzeType === analyzeTypes.analyzeDistricts.value
              ? districtStats[selectedAttribute]
              : storeStats[selectedAttribute]
          }
          selectedAttribute={selectedAttribute}
        ></Chart>
      )}
    </div>
  );
};

/*
ref={mapWrapperRef}
 */

KeplerGLMap.defaultProps = {
  app: {},
  keplerGl: {},
};

KeplerGLMap.propTypes = {
  app: PropTypes.object,
  keplerGl: PropTypes.object,
};

export default KeplerGLMap;
